/**
 * The browser half of a passkey ceremony: base64url<->`ArrayBuffer`
 * conversion around `navigator.credentials`, hand-rolled the same way
 * `sessions.ts` and `magicLink.ts` on the server each have their own copy of
 * the same alphabet -- see docs/notes/decisions.md. The actual cryptography
 * never happens here; the platform authenticator and the worker's
 * `@simplewebauthn/server` do that.
 */

import type {
  PasskeyCreationOptions,
  PasskeyCreationResponse,
  PasskeyRequestOptions,
  PasskeyRequestResponse,
} from "@iltocat/shared";

/** Whether this browser can do any of this at all -- checked before a button is even shown. */
export const passkeysSupported = (): boolean =>
  typeof window !== "undefined" && "PublicKeyCredential" in window;

function toBuffer(base64url: string): ArrayBuffer {
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function fromBuffer(buffer: ArrayBuffer): string {
  let binary = "";
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A cancelled or failed OS prompt is the one error worth its own sentence; everything else is generic. */
export function passkeyErrorMessage(problem: unknown): string {
  if (problem instanceof Error && problem.name === "NotAllowedError") {
    return "Cancelled.";
  }
  return "That did not work. Try again, or sign in a different way.";
}

/**
 * Built field by field rather than `{ ...options, challenge: toBuffer(...) }`:
 * the wire shape's `challenge`/credential ids are base64url strings and the
 * DOM's are `BufferSource`, and a couple of fields (`attestation`,
 * `userVerification`) are plain strings on the wire but string-literal unions
 * in the DOM's own types -- true at runtime by construction, since the
 * worker only ever sends values WebAuthn itself defines, but not something
 * `exactOptionalPropertyTypes` can check field by field through a spread.
 */
function creationOptions(options: PasskeyCreationOptions): CredentialCreationOptions {
  return {
    publicKey: {
      rp: options.rp,
      user: { ...options.user, id: toBuffer(options.user.id) },
      challenge: toBuffer(options.challenge),
      pubKeyCredParams: options.pubKeyCredParams,
      ...(options.timeout !== undefined ? { timeout: options.timeout } : {}),
      ...(options.excludeCredentials
        ? {
            excludeCredentials: options.excludeCredentials.map((entry) => ({
              id: toBuffer(entry.id),
              type: "public-key" as const,
              ...(entry.transports ? { transports: entry.transports } : {}),
            })),
          }
        : {}),
      ...(options.authenticatorSelection ? { authenticatorSelection: options.authenticatorSelection } : {}),
      ...(options.attestation !== undefined ? { attestation: options.attestation } : {}),
    },
  } as CredentialCreationOptions;
}

/** Runs the browser's create ceremony and converts the result back to the JSON the worker expects. */
export async function createPasskey(options: PasskeyCreationOptions): Promise<PasskeyCreationResponse> {
  const credential = (await navigator.credentials.create(
    creationOptions(options),
  )) as PublicKeyCredential | null;
  if (!credential) throw new Error("No passkey was created.");

  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: fromBuffer(credential.rawId),
    type: "public-key",
    response: {
      clientDataJSON: fromBuffer(response.clientDataJSON),
      attestationObject: fromBuffer(response.attestationObject),
      ...(response.getTransports ? { transports: response.getTransports() } : {}),
    },
    clientExtensionResults: {},
  };
}

function requestOptions(options: PasskeyRequestOptions): CredentialRequestOptions {
  return {
    publicKey: {
      challenge: toBuffer(options.challenge),
      ...(options.timeout !== undefined ? { timeout: options.timeout } : {}),
      ...(options.rpId !== undefined ? { rpId: options.rpId } : {}),
      // No allowCredentials by default -- that is what makes sign-in discoverable:
      // the authenticator, not this app, decides which credential to offer.
      ...(options.allowCredentials
        ? {
            allowCredentials: options.allowCredentials.map((entry) => ({
              id: toBuffer(entry.id),
              type: "public-key" as const,
              ...(entry.transports ? { transports: entry.transports } : {}),
            })),
          }
        : {}),
      ...(options.userVerification !== undefined ? { userVerification: options.userVerification } : {}),
    },
  } as CredentialRequestOptions;
}

/** Runs the browser's get ceremony (discoverable: no credential list) and converts the result back. */
export async function getPasskey(options: PasskeyRequestOptions): Promise<PasskeyRequestResponse> {
  const credential = (await navigator.credentials.get(requestOptions(options))) as PublicKeyCredential | null;
  if (!credential) throw new Error("No passkey was offered.");

  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: fromBuffer(credential.rawId),
    type: "public-key",
    response: {
      clientDataJSON: fromBuffer(response.clientDataJSON),
      authenticatorData: fromBuffer(response.authenticatorData),
      signature: fromBuffer(response.signature),
      ...(response.userHandle ? { userHandle: fromBuffer(response.userHandle) } : {}),
    },
    clientExtensionResults: {},
  };
}
